// src/App.js
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FiBookmark, FiRefreshCw, FiSend } from 'react-icons/fi';
import {
    fetchStorageStats,
    fetchVideos,
    incrementVideoViews,
    loadAuth,
    loadFirestore,
    readCachedVideos,
    snapshotVideos,
    videoFromName,
} from './firebase';
import keywords from './components/keywords';
import TopBar from './components/ig/TopBar';
import ProfileHeader from './components/ig/ProfileHeader';
import ProfileTabs, { TABS } from './components/ig/ProfileTabs';
import PostGrid from './components/ig/PostGrid';
import BottomNav from './components/ig/BottomNav';
import './App.css';

const loadVideoModal = () => import('./components/VideoModal');
const VideoModal = lazy(loadVideoModal);
const LoginModal = lazy(() => import('./components/LoginModal'));
const SupportModal = lazy(() => import('./components/SupportModal'));

const allKeywords = [...keywords.primary, ...keywords.secondary, ...keywords.longTail].join(', ');

const POST_PATH = /^\/(?:mobile\/)?videos\/([^/]+)\/?$/;

const sortPosts = (videos, tab, favorites) => {
    switch (tab) {
        case 'top':
            return [...videos].sort((a, b) => b.likes - a.likes);
        case 'viewed':
            return [...videos].sort((a, b) => b.views - a.views);
        case 'saved':
            return videos.filter((v) => favorites.includes(v.videoId));
        default:
            return [...videos].sort((a, b) => b.created - a.created);
    }
};

const initialVideos = () => readCachedVideos() || snapshotVideos();

function ProfilePage() {
    const location = useLocation();
    const navigate = useNavigate();

    const [videos, setVideos] = useState(initialVideos);
    const [status, setStatus] = useState(() => (initialVideos().length ? 'ready' : 'loading'));
    const [statsReady, setStatsReady] = useState(() => initialVideos().length > 0);
    const [tab, setTab] = useState('posts');
    const [user, setUser] = useState(null);
    const [favorites, setFavorites] = useState([]);
    const [showLogin, setShowLogin] = useState(false);
    const [showSupport, setShowSupport] = useState(false);
    const [toast, setToast] = useState(null);
    const openedFromGrid = useRef(false);
    const countedView = useRef(null);
    const toastTimer = useRef(null);

    const load = useCallback(async () => {
        try {
            const base = await fetchVideos();
            setVideos(base);
            setStatus('ready');
            // One metadata request per video; give the first thumbnails the network first.
            await new Promise((resolve) => setTimeout(resolve, 1500));
            setVideos(await fetchStorageStats(base));
            setStatsReady(true);
        } catch (error) {
            console.error('Failed to load posts', error);
            setStatus((current) => (current === 'ready' ? current : 'error'));
        }
    }, []);

    useEffect(() => {
        load();
        const warmModal = setTimeout(loadVideoModal, 2500);
        return () => clearTimeout(warmModal);
    }, [load]);

    useEffect(() => {
        let cancelled = false;
        let unsubscribe = () => {};
        loadAuth().then(({ auth, onAuthStateChanged }) => {
            if (cancelled) return;
            unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
                setUser(nextUser);
                if (!nextUser) {
                    setFavorites([]);
                    return;
                }
                try {
                    const { db, doc, getDoc } = await loadFirestore();
                    const snap = await getDoc(doc(db, 'users', nextUser.uid));
                    setFavorites(snap.exists() ? snap.data().favorites || [] : []);
                } catch (error) {
                    console.error('Failed to load saved posts', error);
                }
            });
        });
        return () => {
            cancelled = true;
            unsubscribe();
        };
    }, []);

    const activeName = useMemo(() => {
        const match = location.pathname.match(POST_PATH);
        return match ? decodeURIComponent(match[1]) : null;
    }, [location.pathname]);

    const activeVideo = useMemo(() => {
        if (!activeName) return null;
        return videos.find((v) => v.videoId === `videos/${activeName}`) || videoFromName(activeName);
    }, [activeName, videos]);

    const activeId = activeVideo?.videoId;
    useEffect(() => {
        if (!activeId) {
            countedView.current = null;
            return;
        }
        if (countedView.current === activeId) return;
        countedView.current = activeId;
        incrementVideoViews(activeId)
            .then((views) => setVideos((cur) => cur.map((v) => (v.videoId === activeId ? { ...v, views } : v))))
            .catch(() => {});
    }, [activeId]);

    const showToast = useCallback((message) => {
        setToast(message);
        clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(null), 2200);
    }, []);

    const openPost = useCallback((video) => {
        openedFromGrid.current = true;
        navigate(`/${video.videoId.split('/').map(encodeURIComponent).join('/')}`);
    }, [navigate]);

    const closePost = useCallback(() => {
        if (openedFromGrid.current) {
            openedFromGrid.current = false;
            navigate(-1);
        } else {
            navigate('/', { replace: true });
        }
    }, [navigate]);

    const updatePost = useCallback((videoId, patch) => {
        setVideos((cur) => cur.map((v) => (v.videoId === videoId ? { ...v, ...patch } : v)));
    }, []);

    const updateFavorite = useCallback((videoId, saved) => {
        setFavorites((cur) => (saved ? [...new Set([...cur, videoId])] : cur.filter((id) => id !== videoId)));
    }, []);

    const selectTab = useCallback((nextTab) => {
        setTab(nextTab);
        const tabs = document.querySelector('.ig-tabs');
        if (tabs && tabs.getBoundingClientRect().top < 0) tabs.scrollIntoView({ behavior: 'smooth' });
    }, []);

    const goHome = useCallback(() => {
        setTab('posts');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const retry = () => {
        setStatus('loading');
        load();
    };

    const shareProfile = async () => {
        const url = window.location.origin;
        try {
            if (navigator.share) {
                await navigator.share({ title: 'Pinkwire', url });
                return;
            }
            await navigator.clipboard.writeText(url);
            showToast('Link copied');
        } catch (error) {
            if (error?.name !== 'AbortError') showToast("Couldn't copy link");
        }
    };

    const posts = useMemo(() => sortPosts(videos, tab, favorites), [videos, tab, favorites]);
    const totals = useMemo(
        () => videos.reduce((t, v) => ({ views: t.views + v.views, likes: t.likes + v.likes }), { views: 0, likes: 0 }),
        [videos]
    );
    const tabLabel = TABS.find((t) => t.id === tab).label;

    let body;
    if (status === 'error') {
        body = (
            <div className="ig-state">
                <span className="ig-state-icon"><FiRefreshCw aria-hidden="true" /></span>
                <h2>Couldn't load posts</h2>
                <p>Check your connection and try again.</p>
                <button type="button" className="ig-btn ig-btn--primary" onClick={retry}>Try again</button>
            </div>
        );
    } else if (tab === 'saved' && !user) {
        body = (
            <div className="ig-state">
                <span className="ig-state-icon"><FiBookmark aria-hidden="true" /></span>
                <h2>Save posts you like</h2>
                <p>Log in to see the posts you've saved.</p>
                <button type="button" className="ig-btn ig-btn--primary" onClick={() => setShowLogin(true)}>Log in</button>
            </div>
        );
    } else if (status === 'ready' && posts.length === 0) {
        body = (
            <div className="ig-state">
                <span className="ig-state-icon"><FiBookmark aria-hidden="true" /></span>
                <h2>{tab === 'saved' ? 'No saved posts yet' : 'No posts yet'}</h2>
                {tab === 'saved' && <p>Tap the bookmark on any post to save it here.</p>}
            </div>
        );
    } else {
        body = <PostGrid posts={posts} loading={status === 'loading'} onOpen={openPost} />;
    }

    return (
        <div className="ig-app">
            <Helmet>
                <title>{`Pinkwire: Browse Videos: ${tabLabel.toUpperCase()} | ${allKeywords}`}</title>
                <meta name="description" content={`Explore our collection of videos under the ${tabLabel} category. Watch videos on ${allKeywords} and more.`} />
                <meta name="keywords" content={allKeywords} />
            </Helmet>

            <TopBar
                user={user}
                onHome={goHome}
                onLogin={() => setShowLogin(true)}
                onLogout={() => loadAuth().then(({ auth, signOut }) => signOut(auth))}
                onSaved={() => selectTab('saved')}
            />

            <main className="ig-main">
                <ProfileHeader
                    loading={status === 'loading'}
                    viewsReady={statsReady}
                    postCount={videos.length}
                    views={totals.views}
                    likes={totals.likes}
                    user={user}
                    onLogin={() => setShowLogin(true)}
                    onSaved={() => selectTab('saved')}
                    onMessage={() => setShowSupport(true)}
                    onShare={shareProfile}
                />
                <ProfileTabs active={tab} onChange={setTab} />
                {body}
            </main>

            <button type="button" className="ig-fab" onClick={() => setShowSupport(true)} aria-label="Message support">
                <FiSend aria-hidden="true" />
            </button>

            <BottomNav
                active={tab}
                user={user}
                onHome={goHome}
                onSelect={selectTab}
                onMessage={() => setShowSupport(true)}
                onProfile={user ? () => selectTab('saved') : () => setShowLogin(true)}
            />

            {toast && <div className="ig-toast" role="status">{toast}</div>}

            <Suspense fallback={null}>
                {activeVideo && (
                    <VideoModal
                        key={activeVideo.videoId}
                        video={activeVideo}
                        onClose={closePost}
                        onPostChange={updatePost}
                        onFavoriteChange={updateFavorite}
                        onToast={showToast}
                    />
                )}
                {showLogin && <LoginModal onClose={() => setShowLogin(false)} onLoginSuccess={() => setShowLogin(false)} />}
                {showSupport && <SupportModal onClose={() => setShowSupport(false)} />}
            </Suspense>
        </div>
    );
}

function App() {
    return (
        <Router>
            <Routes>
                <Route path="*" element={<ProfilePage />} />
            </Routes>
        </Router>
    );
}

export default App;
